/**
 * OpenClaw MCP Server Management
 *
 * Deploy and manage OpenClaw MCP server in user's cloud.
 */

import axios from 'axios';
import { CheckCircle, Loader, RefreshCw, Server, XCircle } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8888';

interface MCPStatus {
  deployment_id: string;
  status: 'deploying' | 'healthy' | 'unhealthy' | 'not_deployed';
  mcp_endpoint: string | null;
  last_health_check: string | null;
  error: string | null;
}

export default function OpenClawMCP() {
  const [provider, setProvider] = useState<string>('cloudflare');
  const [mcpStatus, setMcpStatus] = useState<MCPStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [deploying, setDeploying] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchMCPStatus = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/openclaw/status`, {
        params: { provider },
        headers: { Authorization: `Bearer ${token}` },
      });

      setMcpStatus(response.data as MCPStatus);
    } catch (err) {
      setError((err as any).response?.data?.detail as string | null || 'Failed to fetch MCP status');
    } finally {
      setLoading(false);
    }
  }, [provider]);

  useEffect(() => {
    void fetchMCPStatus();
  }, [provider, fetchMCPStatus]);

  const deployMCP = async () => {
    setDeploying(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/openclaw/deploy`,
        { provider },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Poll for deployment status
      const deploymentId = response.data.deployment_id as string;
      void pollDeploymentStatus(deploymentId);
    } catch (err) {
      setError((err as any).response?.data?.detail as string | null || 'Failed to deploy MCP server');
      setDeploying(false);
    }
  };

  const pollDeploymentStatus = (_deploymentId: string) => {
    const interval = setInterval(() => {
      void (async () => {
        try {
          const token = localStorage.getItem('token');
          const response = await axios.get(`${API_URL}/api/openclaw/status`, {
            params: { provider },
            headers: { Authorization: `Bearer ${token}` },
          });

          setMcpStatus(response.data as MCPStatus);

          if (response.data.status === 'completed' || response.data.status === 'failed') {
            clearInterval(interval);
            setDeploying(false);
          }
        } catch (_err) {
          clearInterval(interval);
          setDeploying(false);
        }
      })();
    }, 3000); // Poll every 3 seconds
  };

  const runHealthCheck = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/openclaw/health-check`,
        null,
        {
          params: { provider },
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      // Refresh status
      await fetchMCPStatus();
    } catch (err) {
      setError((err as any).response?.data?.detail as string | null || 'Health check failed');
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = () => {
    if (!mcpStatus) {
      return <Loader className="animate-spin" size={24} />;
    }

    switch (mcpStatus.status) {
      case 'healthy':
        return <CheckCircle className="text-green-500" size={24} />;
      case 'unhealthy':
        return <XCircle className="text-red-500" size={24} />;
      case 'deploying':
        return <Loader className="animate-spin text-blue-500" size={24} />;
      case 'not_deployed':
        return <Server className="text-gray-400" size={24} />;
      default:
        return <Server className="text-gray-400" size={24} />;
    }
  };

  const getStatusText = () => {
    if (!mcpStatus) {
      return 'Loading...';
    }

    switch (mcpStatus.status) {
      case 'healthy':
        return 'MCP Server is healthy';
      case 'unhealthy':
        return 'MCP Server is unhealthy';
      case 'deploying':
        return 'Deploying MCP Server...';
      case 'not_deployed':
        return 'MCP Server not deployed';
      default:
        return 'Unknown status';
    }
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>
          OpenClaw MCP Server
        </h1>
        <p className="text-sm mt-2" style={{ color: 'var(--text-secondary)' }}>
          Deploy and manage OpenClaw MCP server in your cloud infrastructure
        </p>
      </div>

      {/* Provider Selection */}
      <div className="card mb-6">
        <div className="card-body">
          <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
            Cloud Provider
          </label>
          <select
            value={provider}
            onChange={(e) => setProvider(e.target.value)}
            className="w-full px-4 py-2 rounded-lg border"
            style={{
              background: 'var(--input-bg)',
              color: 'var(--text-primary)',
              borderColor: 'var(--border-color)',
            }}
          >
            <option value="cloudflare">Cloudflare</option>
            <option value="azure">Azure</option>
            <option value="gcp">Google Cloud</option>
            <option value="aws">AWS</option>
          </select>
        </div>
      </div>

      {/* MCP Status */}
      <div className="card mb-6">
        <div className="card-body">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>
              MCP Server Status
            </h2>
            <button
              onClick={() => { void runHealthCheck() }}
              disabled={loading || !mcpStatus || mcpStatus.status === 'not_deployed'}
              className="btn btn-sm btn-secondary flex items-center gap-2"
            >
              <RefreshCw size={16} />
              Health Check
            </button>
          </div>

          <div className="flex items-center gap-4 mb-4">
            {getStatusIcon()}
            <div>
              <p className="font-medium" style={{ color: 'var(--text-primary)' }}>
                {getStatusText()}
              </p>
              {mcpStatus?.mcp_endpoint && (
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                  Endpoint: {mcpStatus.mcp_endpoint}
                </p>
              )}
              {mcpStatus?.last_health_check && (
                <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                  Last check: {new Date(mcpStatus.last_health_check).toLocaleString()}
                </p>
              )}
            </div>
          </div>

          {error && (
            <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/20 mb-4">
              <p className="text-sm text-red-500">{error}</p>
            </div>
          )}

          {mcpStatus?.status === 'not_deployed' && (
            <button
              onClick={() => { void deployMCP() }}
              disabled={deploying}
              className="btn btn-primary w-full"
            >
              {deploying ? (
                <>
                  <Loader className="animate-spin mr-2" size={16} />
                  Deploying...
                </>
              ) : (
                <>
                  <Server className="mr-2" size={16} />
                  Deploy MCP Server
                </>
              )}
            </button>
          )}
        </div>
      </div>

      {/* Info */}
      <div className="card">
        <div className="card-body">
          <h3 className="text-lg font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>
            What is OpenClaw MCP Server?
          </h3>
          <p className="text-sm mb-3" style={{ color: 'var(--text-secondary)' }}>
            The OpenClaw MCP (Model Context Protocol) server runs in your cloud infrastructure
            and handles skill execution requests. It provides:
          </p>
          <ul className="list-disc list-inside text-sm space-y-2" style={{ color: 'var(--text-secondary)' }}>
            <li>Skill execution orchestration</li>
            <li>Data scraping (Facebook, Twitter, Gmail, WhatsApp)</li>
            <li>AI capabilities (web search, data analysis, image generation)</li>
            <li>Direct storage to your cloud (R2, Blob, GCS, S3)</li>
          </ul>
          <p className="text-sm mt-3" style={{ color: 'var(--text-secondary)' }}>
            <strong>Zero-Knowledge:</strong> All data stays in your cloud. AiCloud only orchestrates execution.
          </p>
        </div>
      </div>
    </div>
  );
}
